import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import {TooltipPlacement} from '@vio/atlas/Tooltip'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {markFreezableFilterTooltipShown} from '@vio/shared/src/dealFreeze/utils/FreezableFilterTooltip'

import {messages} from './messages'
import {PaddedTooltip} from './styles'
import {trackFreezableFilterTooltipDisplayed} from './trackingEvents'

const FILTER_CHIP_SELECTOR = '[data-id="roomFilter-dealFreeze"]'
// ~20 frames at 60fps ≈ 330ms — generous enough that the filter chip has
// rendered after the toggle dispatch even on lower-end devices.
const ANCHOR_RETRY_FRAMES = 20

interface Props {
  isOpen: boolean
  onClose: () => void
  content?: string
}

export const FreezableFilterExplanationTooltip: React.FC<Props> = ({
  isOpen,
  onClose,
  content
}) => {
  const intl = useIntl()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const [anchor, setAnchor] = useState<Element | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const hasFiredDisplayedRef = useRef(false)

  /**
   * Resolves the filter chip the tooltip anchors to. The chip may not be in the
   * DOM yet when `isOpen` flips true, so we poll across animation frames (up to
   * ANCHOR_RETRY_FRAMES) until it appears, then store it as the anchor. Cleanup
   * cancels both the pending frame and any in-flight callback to avoid setting
   * state after unmount.
   */
  useEffect(() => {
    if (!isOpen) {
      setAnchor(null)
      return
    }

    let cancelled = false
    let remainingFrames = ANCHOR_RETRY_FRAMES
    let rafId = 0

    const tryResolve = () => {
      if (cancelled) return
      const found = document.querySelector(FILTER_CHIP_SELECTOR)
      if (found) {
        setAnchor(found)
        return
      }
      if (remainingFrames-- > 0) {
        rafId = window.requestAnimationFrame(tryResolve)
      }
    }

    tryResolve()
    return () => {
      cancelled = true
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !anchor || hasFiredDisplayedRef.current) return
    hasFiredDisplayedRef.current = true
    markFreezableFilterTooltipShown()
    trackFreezableFilterTooltipDisplayed(dispatchTeamEvent)
  }, [isOpen, anchor, dispatchTeamEvent])

  useEffect(() => {
    if (!isOpen || !anchor) return
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (anchor.contains(target)) return
      if (containerRef.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [isOpen, anchor, onClose])

  if (!isOpen || !anchor) return null

  return (
    <div ref={containerRef} data-id="dealFreezeFilterTooltip">
      <PaddedTooltip
        isOpen
        anchor={anchor}
        placement={TooltipPlacement.Bottom}
        content={content ?? intl.formatMessage(messages.filterExplanationBody)}
        onClose={onClose}
      />
    </div>
  )
}
